/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { SoftPressable } from '../ui/SoftPressable';
import { ticketOperationalHeader as oh } from './ticketOperationalHeader.styles';
import { ticketColors } from '../theme/public-api';

export type TicketKpiItem = {
  label: string;
  value: string;
  tone?: 'default' | 'ok' | 'warn' | 'bad';
  /** Si está set, el tile es botón (ej. filtrar alertas). */
  onPress?: () => void;
  /** Tile seleccionado (filtro activo). */
  selected?: boolean;
  accessibilityLabel?: string;
  testID?: string;
};

type Props = {
  items: TicketKpiItem[];
  /** desk strip (wide) vs mobile cell grid */
  layout?: 'desk' | 'mobile';
  /**
   * `line` = strip clásico con bordes (legacy).
   * `soft` = tiles soft-teal / paper (Square).
   */
  variant?: 'line' | 'soft';
  style?: StyleProp<ViewStyle>;
};

/** Mobile: 2 cols for 2/4; 3 cols only for 3 or 6 (filas completas). 5 → tratar como 4 en callers. */
function mobileCols(count: number): 2 | 3 {
  if (count === 3 || count === 6) return 3;
  return 2;
}

function ValueFirstContent({
  item,
  numStyle,
  labStyle,
}: {
  item: TicketKpiItem;
  numStyle: object;
  labStyle: object;
}) {
  return (
    <>
      <Text
        style={[
          numStyle,
          item.tone === 'ok' && oh.kpiStripNumOk,
          item.tone === 'warn' && oh.kpiStripNumWarn,
          item.tone === 'bad' && oh.kpiStripNumBad,
        ]}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.65}
      >
        {item.value}
      </Text>
      <Text style={labStyle}>{item.label}</Text>
    </>
  );
}

/** Proto Square: label arriba, valor abajo. */
function LabelFirstContent({
  item,
  numStyle,
  labStyle,
}: {
  item: TicketKpiItem;
  numStyle: object;
  labStyle: object;
}) {
  const warn = item.tone === 'warn' && !item.selected;
  const bad = item.tone === 'bad' && !item.selected;
  return (
    <>
      <Text
        style={[
          labStyle,
          warn && oh.kpiSoftLabWarn,
          bad && oh.kpiSoftLabBad,
        ]}
      >
        {item.label}
      </Text>
      <Text
        style={[
          numStyle,
          item.tone === 'ok' && oh.kpiStripNumOk,
          warn && oh.kpiSoftNumWarn,
          bad && oh.kpiSoftNumBad,
        ]}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.65}
      >
        {item.value}
      </Text>
    </>
  );
}

function renderCell(
  item: TicketKpiItem,
  soft: boolean,
  cellStyle: StyleProp<ViewStyle>,
  numStyle: object,
  labStyle: object,
) {
  const a11y =
    item.accessibilityLabel ??
    `${item.label}: ${item.value}${item.tone === 'bad' ? ' (alerta)' : item.tone === 'warn' ? ' (atención)' : ''}`;
  const Content = soft ? LabelFirstContent : ValueFirstContent;
  const body = <Content item={item} numStyle={numStyle} labStyle={labStyle} />;

  if (item.onPress) {
    return (
      <SoftPressable
        key={item.label}
        style={cellStyle}
        onPress={item.onPress}
        accessibilityRole="button"
        accessibilityLabel={a11y}
        accessibilityState={{ selected: Boolean(item.selected) }}
        testID={item.testID}
      >
        {body}
      </SoftPressable>
    );
  }

  return (
    <View
      key={item.label}
      style={cellStyle}
      accessible
      accessibilityRole="text"
      accessibilityLabel={a11y}
      testID={item.testID}
    >
      {body}
    </View>
  );
}

export function TicketKpiStrip({
  items,
  layout = 'desk',
  variant = 'line',
  style,
}: Props) {
  if (items.length === 0) return null;

  const soft = variant === 'soft';

  if (layout === 'mobile') {
    const cols = mobileCols(items.length);
    // Soft 2×2 / 3-col: flexBasis evita que gap+width% deforme las celdas.
    const softCellFlex =
      cols === 2
        ? ({ flexBasis: '47%' as const, maxWidth: '48%' as const })
        : ({ flexBasis: '30%' as const, maxWidth: '32%' as const });
    const cellWidth = cols === 2 ? ('50%' as const) : ('33.333%' as const);

    return (
      <View
        style={[soft ? oh.kpiSoftGridMobile : oh.kpiGridMobile, style]}
        accessibilityLabel="Métricas"
      >
        <View style={soft ? oh.kpiSoftRowMobile : oh.kpiRowMobile}>
          {items.map((item) =>
            renderCell(
              item,
              soft,
              [
                soft ? oh.kpiSoftCellMobile : oh.kpiCellMobile,
                soft ? softCellFlex : { width: cellWidth },
                item.tone === 'warn' && soft && !item.selected && oh.kpiSoftCellWarn,
                item.tone === 'bad' && soft && !item.selected && oh.kpiSoftCellBad,
                item.selected && oh.kpiSoftCellSelected,
              ],
              soft
                ? [oh.kpiSoftNumMobile, item.value.length > 7 && oh.kpiSoftNumMobileCompact]
                : [oh.kpiNumMobile, item.value.length > 7 && oh.kpiSoftNumMobileCompact],
              soft ? oh.kpiSoftLabMobile : oh.kpiLabelMobile,
            ),
          )}
        </View>
      </View>
    );
  }

  if (soft) {
    return (
      <View style={[oh.kpiSoftStrip, style]} accessibilityLabel="Métricas">
        {items.map((item) =>
          renderCell(
            item,
            true,
            [
              oh.kpiSoftCell,
              item.tone === 'warn' && !item.selected && oh.kpiSoftCellWarn,
              item.tone === 'bad' && !item.selected && oh.kpiSoftCellBad,
              item.selected && oh.kpiSoftCellSelected,
            ],
            oh.kpiSoftNum,
            oh.kpiSoftLab,
          ),
        )}
      </View>
    );
  }

  return (
    <View style={[oh.kpiStrip, style]}>
      {items.map((item, idx) =>
        renderCell(
          item,
          false,
          [
            oh.kpiStripCell,
            idx === items.length - 1 && oh.kpiStripCellLast,
            item.selected && { backgroundColor: ticketColors.brandSoft },
          ],
          oh.kpiStripNum,
          oh.kpiStripLab,
        ),
      )}
    </View>
  );
}
